import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { authApi, dashboardApi, sendApi, staffApi, templatesApi, customerApi } from "@/api";
import type { CustomerSearchResult } from "@/types";
import {
  LineChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  ComposedChart,
} from "recharts";
import { countSmsChars, SMS_PREFIX } from "@/utils/sms";
import HelpTip from "@/components/ui/HelpTip";

function StatCard({
  label,
  value,
  sub,
  tip,
}: {
  label: string;
  value: string | number;
  sub?: string;
  tip?: string;
}) {
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border">
      <p className="text-sm text-slate-500">{label}{tip && <HelpTip text={tip} />}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

function QuickSend() {
  const [to, setTo] = useState("");
  const [body, setBody] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [result, setResult] = useState<{ ok?: boolean; error?: string } | null>(null);
  const [manualName, setManualName] = useState("");
  const [manualCompany, setManualCompany] = useState("");

  // 顧客検索
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerResults, setCustomerResults] = useState<CustomerSearchResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSearchResult | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const { data: staffData } = useQuery({
    queryKey: ["staff"],
    queryFn: () => staffApi.list(),
  });

  // ログインユーザー情報を取得
  const { data: meData } = useQuery({
    queryKey: ["auth-me"],
    queryFn: () => authApi.me(),
  });

  const staffId = meData?.staffId ?? "";
  const { data: templateData } = useQuery({
    queryKey: ["templates"],
    queryFn: () => templatesApi.list(),
  });

  const selectedStaff = useMemo(
    () => staffData?.data.find((s) => s.id === staffId),
    [staffData, staffId],
  );

  // 顧客検索デバウンス
  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) {
      setCustomerResults([]);
      setShowDropdown(false);
      return;
    }
    try {
      const res = await customerApi.search(q);
      setCustomerResults(res.data);
      setShowDropdown(res.data.length > 0);
    } catch {
      setCustomerResults([]);
    }
  }, []);

  const handleCustomerQueryChange = (val: string) => {
    setCustomerQuery(val);
    setSelectedCustomer(null);
    // 顧客解除時にテンプレート本文を再展開（前回顧客名のクリア）
    const tpl = templateData?.data.find((t) => t.id === templateId);
    if (tpl) {
      setBody(expandPreview(tpl.body, selectedStaff ?? null, null));
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleSelectCustomer = (c: CustomerSearchResult) => {
    setSelectedCustomer(c);
    setManualName(c.contactName || c.traderName);
    setManualCompany(c.companyName || "");
    const displayName = c.contactName || c.traderName;
    setCustomerQuery(c.companyName ? `${c.companyName} ${displayName}` : displayName);
    setTo(c.cellPhone || c.phone || "");
    setShowDropdown(false);
    // テンプレート選択済みなら顧客変数で本文を再展開
    const tpl = templateData?.data.find((t) => t.id === templateId);
    if (tpl) {
      setBody(expandPreview(tpl.body, selectedStaff ?? null, c));
    }
  };

  // ドロップダウン外クリックで閉じる
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // テンプレートプレビュー展開ヘルパー
  const expandPreview = (tplBody: string, staff: typeof selectedStaff | null, customer: typeof selectedCustomer | null) => {
    let preview = tplBody;
    if (staff) {
      preview = preview.replace(/\{\{staff_name\}\}/g, staff.name);
      preview = preview.replace(/\{\{staff_name_last\}\}/g, staff.name);
      preview = preview.replace(/\{\{booking_url\}\}/g, "(予約リンク)");
    }
    if (customer) {
      preview = preview
        .replace(/\{\{company_name\}\}/g, customer.companyName || "")
        .replace(/\{\{contact_name\}\}/g, customer.contactName || customer.traderName || "")
        .replace(/\{\{customer_name\}\}/g, customer.contactName || customer.traderName || "");
    }
    // 未展開の変数をクリア
    preview = preview.replace(/\{\{\w+\}\}/g, "");
    return preview;
  };

  // テンプレート選択時に本文をプレビュー展開
  const handleTemplateChange = (tid: string) => {
    setTemplateId(tid);
    const tpl = templateData?.data.find((t) => t.id === tid);
    if (tpl) {
      setBody(expandPreview(tpl.body, selectedStaff, selectedCustomer));
    }
  };


  const mutation = useMutation({
    mutationFn: (data: Parameters<typeof sendApi.send>[0]) =>
      sendApi.send(data),
    onSuccess: () => {
      setResult({ ok: true });
      setTo("");
      setBody("");
      setTemplateId("");
      setManualName("");
      setManualCompany("");
      setCustomerQuery("");
      setSelectedCustomer(null);
      setTimeout(() => setResult(null), 3000);
    },
    onError: (err: any) => {
      setResult({ error: err?.message || "送信に失敗しました" });
    },
  });

  const handleSend = () => {
    if (!to.trim() || !body.trim()) return;
    mutation.mutate({
      to,
      body,
      staffId: staffId || undefined,
      templateId: templateId || undefined,
      customerId: selectedCustomer?.traderId,
      customerName: manualName || undefined,
      companyName: manualCompany || undefined,
    });
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border">
      <h3 className="text-sm font-medium mb-3">クイック送信</h3>
      <div className="flex gap-3 items-end flex-wrap">
        {/* 顧客検索 */}
        <div className="flex-shrink-0 relative" ref={searchRef}>
          <label className="block text-xs text-slate-500 mb-1">顧客検索</label>
          <input
            type="text"
            value={customerQuery}
            onChange={(e) => handleCustomerQueryChange(e.target.value)}
            onFocus={() => customerResults.length > 0 && setShowDropdown(true)}
            placeholder="会社名・名前・電話番号"
            className="w-56 px-3 py-2 border rounded-md text-sm"
          />
          {showDropdown && (
            <div className="absolute z-20 top-full left-0 w-80 mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
              {customerResults.map((c) => (
                <button
                  key={`${c.traderId}-${c.contactId ?? "t"}`}
                  onClick={() => handleSelectCustomer(c)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0"
                >
                  <div className="text-sm font-medium">
                    {c.companyName && <span className="text-slate-600">{c.companyName} </span>}
                    {c.contactName || c.traderName}
                  </div>
                  {(c.department || c.title) && (
                    <div className="text-xs text-slate-400">
                      {[c.department, c.title].filter(Boolean).join(" / ")}
                    </div>
                  )}
                  <div className="text-xs text-slate-400">
                    {c.cellPhone || c.phone || "電話番号なし"}
                    {c.traderTypeName && ` / ${c.traderTypeName}`}
                    {c.lastOrderDate && ` / 最終: ${c.lastOrderDate.slice(0, 10)}`}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex-shrink-0">
          <label className="block text-xs text-slate-500 mb-1">宛先</label>
          <input
            type="tel"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="08012345678"
            className="w-44 px-3 py-2 border rounded-md text-sm"
          />
        </div>
        <div className="flex-shrink-0">
          <label className="block text-xs text-slate-500 mb-1">会社名</label>
          <input
            type="text"
            value={manualCompany}
            onChange={(e) => setManualCompany(e.target.value)}
            placeholder="任意"
            className="w-36 px-3 py-2 border rounded-md text-sm"
          />
        </div>
        <div className="flex-shrink-0">
          <label className="block text-xs text-slate-500 mb-1">宛先名</label>
          <input
            type="text"
            value={manualName}
            onChange={(e) => setManualName(e.target.value)}
            placeholder="お客様名"
            className="w-32 px-3 py-2 border rounded-md text-sm"
          />
        </div>
        <div className="flex-shrink-0">
          <label className="block text-xs text-slate-500 mb-1">テンプレート</label>
          <select
            value={templateId}
            onChange={(e) => handleTemplateChange(e.target.value)}
            className="w-48 px-3 py-2 border rounded-md text-sm"
          >
            <option value="">自由入力</option>
            {templateData?.data
              .filter((t) => t.active)
              .map((t) => (
                <option key={t.id} value={t.id}>{t.slug}</option>
              ))}
          </select>
        </div>
      </div>
      {selectedCustomer && (
        <div className="mt-2 text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded">
          選択中: {selectedCustomer.companyName} {selectedCustomer.traderName}
          {selectedCustomer.traderTypeName && ` (${selectedCustomer.traderTypeName})`}
        </div>
      )}
      <div className="flex gap-3 items-end mt-3">
        <div className="flex-1">
          <label className="block text-xs text-slate-500 mb-1">本文</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="メッセージを入力..."
            rows={3}
            className={`w-full px-3 py-2 border rounded-md text-sm resize-none ${
              body && countSmsChars(body).overLimit ? "border-red-400" : ""
            }`}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.metaKey) handleSend();
            }}
          />
          {body.length > 0 && (() => {
            const info = countSmsChars(body);
            return (
              <div className={`flex justify-between text-xs mt-1 ${
                info.overLimit ? "text-red-600" : info.segments >= 2 ? "text-amber-600" : "text-slate-400"
              }`}>
                <span>
                  {SMS_PREFIX.length}+{body.length}={info.estimatedChars}文字 / {info.segments}通（{(info.segments * 3.3).toFixed(1)}円）
                </span>
                <span>
                  {info.segments >= 2 && "70文字超で複数通課金"}
                </span>
              </div>
            );
          })()}
        </div>
        <button
          onClick={handleSend}
          disabled={mutation.isPending || !to.trim() || !body.trim() || (body ? countSmsChars(body).overLimit : false)}
          className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50 whitespace-nowrap self-end"
        >
          {mutation.isPending ? "送信中..." : "送信"}
        </button>
      </div>
      {result?.ok && (
        <p className="text-xs text-green-600 mt-2">送信しました</p>
      )}
      {result?.error && (
        <p className="text-xs text-red-600 mt-2">{result.error}</p>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: () => dashboardApi.stats(),
    refetchInterval: 60000,
  });

  if (isLoading || !data) {
    return <p className="text-slate-500">読み込み中...</p>;
  }

  const { today, daily, summary } = data;
  const successRate =
    today.sent > 0
      ? Math.round((today.delivered / today.sent) * 100)
      : 0;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">ダッシュボード</h2>

      <QuickSend />

      {/* 本日の統計 */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="本日送信" value={today.sent} sub={`${today.segments || today.sent}通分`} tip="送信メッセージ数。長文は複数通としてカウント" />
        <StatCard label="成功率" value={`${successRate}%`} sub={today.failed > 0 ? `失敗: ${today.failed}件` : undefined} tip="配信成功した割合" />
        <StatCard
          label="コスト"
          value={`${(today.costJpy ?? 0).toLocaleString()}円`}
          sub={today.multiSegment > 0 ? `うち複数通: ${today.multiSegment}件` : undefined}
          tip="70文字=1通3.3円、以降67文字ごとに+1通"
        />
        <StatCard
          label="返信"
          value={`${summary?.inboundCount ?? 0}件`}
          sub={summary?.replyRate > 0 ? `返信率: ${summary.replyRate}%` : "返信率: —"}
          tip="顧客からSMSで返信があった件数"
        />
      </div>

      {/* 期間サマリー */}
      {summary && (
        <div className="grid grid-cols-5 gap-3">
          <div className="bg-white px-4 py-3 rounded-lg shadow-sm border text-center">
            <p className="text-xs text-slate-500">期間送信数</p>
            <p className="text-lg font-bold">{summary.totalSent}</p>
          </div>
          <div className="bg-white px-4 py-3 rounded-lg shadow-sm border text-center">
            <p className="text-xs text-slate-500">期間コスト</p>
            <p className="text-lg font-bold">{summary.totalCostJpy.toLocaleString()}円</p>
          </div>
          <div className="bg-white px-4 py-3 rounded-lg shadow-sm border text-center">
            <p className="text-xs text-slate-500">複数通メッセージ<HelpTip text="70文字超で2通以上になった送信の割合。高いとコスト増" /></p>
            <p className={`text-lg font-bold ${summary.multiSegmentRate > 30 ? "text-amber-600" : ""}`}>
              {summary.multiSegmentRate}%
            </p>
            <p className="text-xs text-slate-400">{summary.multiSegmentCount}件</p>
          </div>
          <div className="bg-white px-4 py-3 rounded-lg shadow-sm border text-center">
            <p className="text-xs text-slate-500">返信率<HelpTip text="送信に対して顧客から返信があった割合" /></p>
            <p className="text-lg font-bold">{summary.replyRate}%</p>
            <p className="text-xs text-slate-400">{summary.inboundCount}件</p>
          </div>
          <div className="bg-white px-4 py-3 rounded-lg shadow-sm border text-center">
            <p className="text-xs text-slate-500">予約数<HelpTip text="SMS経由で予約が確定した件数" /></p>
            <p className="text-lg font-bold">{summary.bookingCount}</p>
            <p className="text-xs text-slate-400">予約率: {summary.bookingRate}%</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <h3 className="text-sm font-medium mb-4">日別送信数</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={daily}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="sent"
                name="送信"
                stroke="#3b82f6"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="delivered"
                name="配信済"
                stroke="#22c55e"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="failed"
                name="失敗"
                stroke="#ef4444"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <h3 className="text-sm font-medium mb-4">日別コスト</h3>
          <ResponsiveContainer width="100%" height={250}>
            <ComposedChart data={daily}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} unit="円" />
              <Tooltip formatter={(value) => [`${Number(value).toLocaleString()}円`, ""]} />
              <Legend />
              <Area
                type="monotone"
                dataKey="costJpy"
                name="コスト"
                fill="#dbeafe"
                stroke="#3b82f6"
                strokeWidth={2}
              />
              <Bar
                dataKey="multiSegment"
                name="複数通"
                fill="#f59e0b"
                barSize={12}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
